import { Ref, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useSurveyTranslation } from 'hooks/useSurveyTranslation.ts';
import { Survey, SurveyQuestion } from 'types/survey';
import { Answer } from 'types/surveyResponse';
import { TextInput } from 'components/ui/TextInput';
import { Dropdown } from 'components/ui/Dropdown';
import { getNumericValue } from '../helpers';
import styles from './NumericUnitsQuestion.module.scss';

interface IProps {
  surveyDefinition: Survey;
  question: SurveyQuestion;
  answer: Answer;
  onChange: (answer: Answer) => void;
}

export const NumericUnitsQuestion = ({ surveyDefinition, question, answer, onChange }: IProps) => {
  let defaultValue: [string, number] = [question.options![0].value, 0];
  if (typeof answer.value === 'string' && answer.value.indexOf('|') !== -1) {
    const defaultValueStr = answer.value.split('|');
    defaultValue = [defaultValueStr[0], parseFloat(defaultValueStr[1])];
  }

  const ts = useSurveyTranslation(surveyDefinition);
  const [unit, setUnit] = useState(defaultValue[0]);
  const [value, setValue] = useState(defaultValue[1].toString());
  const [valid, setValid] = useState(true);
  const onChangeDebounced = useDebouncedCallback(
    (unit, value) => onChange({ ...answer, value: `${unit}|${value}` }),
    1500,
    { maxWait: 10000 }
  );
  const inputRef = useRef<HTMLInputElement>();

  const onInput = (value: string) => {
    value = value.replace(/,/g, '.');
    setValue(value);
    const numValue = getNumericValue(value);

    if (
      isNaN(numValue) ||
      (question.minValue && numValue < question.minValue) ||
      (question.maxValue && numValue > question.maxValue)
    ) {
      setValid(false);
    } else {
      setValid(true);
      onChangeDebounced(unit, numValue);
    }
  };

  const onAdd = (add: number) => {
    let numValue = getNumericValue(value);
    if (
      isNaN(numValue) ||
      (question.minValue && numValue < question.minValue) ||
      (question.maxValue && numValue > question.maxValue)
    ) {
      if (typeof answer.value === 'number') {
        numValue = answer.value;
      } else {
        numValue = defaultValue[1];
      }
    }

    numValue += add;
    if (
      (typeof question.minValue !== 'number' || numValue >= question.minValue) &&
      (typeof question.maxValue !== 'number' || numValue <= question.maxValue)
    ) {
      setValid(true);
      setValue(numValue.toString());
      onChangeDebounced(unit, numValue);
    }
  };

  const onSetUnit = (unit: string) => {
    setUnit(unit);
    onChangeDebounced(unit, value);
  };

  useEffect(() => {
    const numValueString = getNumericValue(value).toString();
    inputRef.current!.size = numValueString.length > 0 ? numValueString.length : 1;
  }, [value]);

  // make sure to do one last update on component unmount
  useEffect(() => {
    return () => {
      onChangeDebounced.flush();
    };
  }, []);

  return (
    <div className={styles.numericUnitsQuestion}>
      <Dropdown
        options={question.options!.map((o) => ({ label: ts(o.name), value: o.value }))}
        value={unit}
        onChange={onSetUnit}
      />
      <TextInput
        name={'question-' + question.questionId}
        text={value}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            onAdd(1);
            e.preventDefault();
          }
          if (e.key === 'ArrowDown') {
            onAdd(-1);
            e.preventDefault();
          }
        }}
        state={valid ? undefined : 'error'}
        inputRef={inputRef as Ref<HTMLInputElement | HTMLTextAreaElement>}
      />
    </div>
  );
};
